import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as MiniCalendar } from "@/components/ui/calendar";
import { da } from "date-fns/locale";
import { toast } from "sonner";
import { QrCode, Plus, Trash2, Printer, Users, Clock, Calendar as CalendarIcon } from "lucide-react";

// Formatér Date → YYYY-MM-DD i lokal tid (undgå UTC-skift)
const fmtDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/** Stamdata-fane: generér QR-koder pr. team pr. dag til hurtig indtjekning af frivillige.
 *  Frivillige scanner QR → indtaster fulde navn → automatisk login (ingen password).
 *  Bevarer alle eksisterende team-rettigheder, vagtplan og kapacitetstjek.
 */
export default function HurtigIndtjekningTab({ token, eventId, onCountChange, isAdmin }) {
  const [teams, setTeams] = useState([]);
  const [existingQrs, setExistingQrs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTeamIds, setSelectedTeamIds] = useState(new Set());
  const [startDate, setStartDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 4);
    return d.toISOString().slice(0, 10);
  });
  const [dailyStart, setDailyStart] = useState("06:00");
  const [dailyEnd, setDailyEnd] = useState("02:00");
  const [sessionHours, setSessionHours] = useState(6);

  const loadData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [teamsRes, qrsRes] = await Promise.all([
        axios.get(`${API}/events/${eventId}/roles`),
        axios.get(`${API}/quick-checkin/list?event_id=${eventId}`, { headers }),
      ]);
      setTeams(teamsRes.data || []);
      setExistingQrs(qrsRes.data || []);
      if (onCountChange) onCountChange(qrsRes.data?.length || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fejl ved indlæsning");
    }
  };

  useEffect(() => { if (eventId) loadData(); /* eslint-disable-next-line */ }, [eventId]);

  const toggleTeam = (id) => {
    setSelectedTeamIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAllTeams = () => setSelectedTeamIds(new Set(teams.map(t => t.id)));
  const selectNoTeams = () => setSelectedTeamIds(new Set());

  const handleGenerate = async () => {
    if (selectedTeamIds.size === 0) {
      toast.error("Vælg mindst ét team");
      return;
    }
    if (!startDate || !endDate) {
      toast.error("Vælg start- og slutdato");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/quick-checkin/generate`, {
        event_id: eventId,
        role_ids: Array.from(selectedTeamIds),
        start_date: startDate,
        end_date: endDate,
        daily_start_time: dailyStart,
        daily_end_time: dailyEnd,
        session_duration_hours: parseFloat(sessionHours) || 6,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Genererede ${res.data.created} QR-koder`);
      await loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fejl ved generering");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (qrToken) => {
    if (!window.confirm("Slet denne QR-kode?")) return;
    try {
      await axios.delete(`${API}/quick-checkin/${qrToken}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("QR slettet");
      await loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fejl ved sletning");
    }
  };

  const openPrintView = () => {
    if (existingQrs.length === 0) {
      toast.error("Ingen QR-koder at printe");
      return;
    }
    window.open(`/print-quick-checkin?event_id=${eventId}`, "_blank");
  };

  // Gruppér QR'er pr. dato for visning
  const qrsByDate = {};
  existingQrs.forEach(q => {
    if (!qrsByDate[q.valid_date]) qrsByDate[q.valid_date] = [];
    qrsByDate[q.valid_date].push(q);
  });
  const sortedDates = Object.keys(qrsByDate).sort();

  if (!isAdmin) {
    return (
      <div className="text-center py-16 text-muted-foreground">
        Kun admin har adgang til Hurtig indtjekning
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="quick-checkin-tab">
      {/* Generér nye QR-koder */}
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="flex items-center gap-2 mb-4">
          <Plus className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold">Generér nye QR-koder</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="text-xs uppercase text-muted-foreground block mb-1">Startdato</label>
            <div className="flex gap-1">
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="flex-1 bg-background border border-border rounded px-3 py-2 text-sm"
                data-testid="qc-start-date"
              />
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="icon" className="shrink-0" aria-label="Vælg startdato fra mini-kalender" data-testid="qc-start-date-picker-btn">
                    <CalendarIcon className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="end">
                  <MiniCalendar
                    mode="single"
                    locale={da}
                    weekStartsOn={1}
                    selected={startDate ? new Date(startDate + 'T00:00:00') : undefined}
                    onSelect={(d) => { if (d) setStartDate(fmtDate(d)); }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
          <div>
            <label className="text-xs uppercase text-muted-foreground block mb-1">Slutdato</label>
            <div className="flex gap-1">
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="flex-1 bg-background border border-border rounded px-3 py-2 text-sm"
                data-testid="qc-end-date"
              />
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="icon" className="shrink-0" aria-label="Vælg slutdato fra mini-kalender" data-testid="qc-end-date-picker-btn">
                    <CalendarIcon className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="end">
                  <MiniCalendar
                    mode="single"
                    locale={da}
                    weekStartsOn={1}
                    selected={endDate ? new Date(endDate + 'T00:00:00') : undefined}
                    onSelect={(d) => { if (d) setEndDate(fmtDate(d)); }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
          <div>
            <label className="text-xs uppercase text-muted-foreground block mb-1">Daglig start-tid</label>
            <input
              type="time"
              value={dailyStart}
              onChange={(e) => setDailyStart(e.target.value)}
              className="w-full bg-background border border-border rounded px-3 py-2 text-sm"
              data-testid="qc-daily-start"
            />
          </div>
          <div>
            <label className="text-xs uppercase text-muted-foreground block mb-1">
              Daglig slut-tid <span className="text-muted-foreground">(efter midnat = næste dag)</span>
            </label>
            <input
              type="time"
              value={dailyEnd}
              onChange={(e) => setDailyEnd(e.target.value)}
              className="w-full bg-background border border-border rounded px-3 py-2 text-sm"
              data-testid="qc-daily-end"
            />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs uppercase text-muted-foreground block mb-1 flex items-center gap-1">
              <Clock className="h-3 w-3" /> Session-længde pr. frivillig (timer)
            </label>
            <input
              type="number"
              value={sessionHours}
              onChange={(e) => setSessionHours(e.target.value)}
              min="1"
              max="24"
              step="0.5"
              className="w-full bg-background border border-border rounded px-3 py-2 text-sm"
              data-testid="qc-session-hours"
            />
            <p className="text-[10px] text-muted-foreground mt-1">
              Når en frivillig scanner, er de logget ind i dette antal timer. Auto-logout derefter.
              Heartland: 6 t. · Nibe: 9 eller 12 t.
            </p>
          </div>
        </div>

        {/* Team-vælger */}
        <div className="border-t border-border pt-3">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <label className="text-xs uppercase text-muted-foreground flex items-center gap-1">
              <Users className="h-3 w-3" /> Vælg teams ({selectedTeamIds.size} / {teams.length})
            </label>
            <div className="flex gap-1">
              <Button size="sm" variant="outline" onClick={selectAllTeams} className="h-7 text-xs" data-testid="qc-select-all-teams">Alle</Button>
              <Button size="sm" variant="outline" onClick={selectNoTeams} className="h-7 text-xs" data-testid="qc-select-no-teams">Ingen</Button>
            </div>
          </div>
          <div className="max-h-48 overflow-y-auto grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-1 border border-border rounded p-2 bg-background/50">
            {teams.length === 0 ? (
              <p className="col-span-full text-sm text-muted-foreground italic text-center py-4">
                Ingen teams oprettet på dette event. Opret teams i fanen "Teams" først.
              </p>
            ) : teams.map(t => {
              const checked = selectedTeamIds.has(t.id);
              return (
                <label key={t.id} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-muted/30 cursor-pointer text-sm">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleTeam(t.id)}
                    data-testid={`qc-team-${t.id}`}
                  />
                  <span className="truncate flex-1">{t.name}</span>
                  {t.max_capacity ? (
                    <span className="text-[10px] text-muted-foreground font-mono">{t.max_capacity}p</span>
                  ) : null}
                </label>
              );
            })}
          </div>
        </div>

        <Button onClick={handleGenerate} disabled={loading || selectedTeamIds.size === 0} className="w-full mt-4" data-testid="qc-generate-btn">
          {loading ? "Genererer..." : `Generér QR-koder (${selectedTeamIds.size} teams × dage)`}
        </Button>
        <p className="text-[10px] text-muted-foreground mt-1.5 text-center">
          ⚠️ Genererer du for et tidsrum der allerede har QR'er, bliver de gamle erstattet.
        </p>
      </div>

      {/* Eksisterende QR'er */}
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <QrCode className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold">Eksisterende QR-koder ({existingQrs.length})</h2>
          </div>
          {existingQrs.length > 0 && (
            <Button onClick={openPrintView} data-testid="qc-print-all-btn">
              <Printer className="h-4 w-4 mr-1" /> Print QR-koder (vælg teams)
            </Button>
          )}
        </div>

        {existingQrs.length === 0 ? (
          <p className="text-sm text-muted-foreground italic text-center py-8">
            Ingen QR-koder endnu. Generér dem ovenfor.
          </p>
        ) : (
          <div className="space-y-4">
            {sortedDates.map(date => (
              <div key={date}>
                <h3 className="text-sm font-mono uppercase tracking-wide text-muted-foreground mb-2">
                  {new Date(date).toLocaleDateString('da-DK', { weekday: 'long', day: '2-digit', month: 'short' })}
                  <span className="ml-2 text-xs">({qrsByDate[date].length} teams)</span>
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                  {qrsByDate[date].map(qr => (
                    <div
                      key={qr.token}
                      className="border border-border rounded p-2 flex items-center justify-between gap-2 bg-background/50"
                      data-testid={`qc-qr-${qr.token}`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium truncate">{qr.role_name}</div>
                        <div className="text-[10px] text-muted-foreground font-mono">
                          {qr.valid_from?.slice(11, 16)} → {qr.valid_until?.slice(11, 16)}
                          {qr.capacity ? ` · max ${qr.capacity}p` : ''}
                          {' · '}{qr.session_duration_hours}t/session
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(qr.token)}
                        className="h-7 w-7 p-0 text-red-500 hover:bg-red-500/10"
                        data-testid={`qc-delete-${qr.token}`}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
